import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, Plus, Search, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/modal";
import { cn } from "@/lib/utils/cn";

export interface PickerAction {
  id: string;
  name: string;
  goal: string;
  issue: string;
}

interface ActionCatalog {
  issues: string[];
  goalsByIssue: Record<string, string[]>;
  actions: PickerAction[];
}

interface CatalogQuery {
  data?: ActionCatalog;
  isPending: boolean;
  isError: boolean;
}

/**
 * Controlled action picker shared by the tools & financial-aids admin forms: the
 * list of linked actions in questionnaire order (each with the action's reorder
 * button, via ``rowAction``, and a remove button) and an
 * "add actions" modal with a search box and cascading searchable enjeu/objectif
 * filters + multi-select. The list is a set — where a resource appears among an
 * action's tools/aids is set per action, in the reorder dialog, not here.
 * Mirrors the Django back-office picker. The i18n namespace (``ns``) and the
 * action-catalogue query hook are injected so a single implementation serves
 * both apps.
 */
export function ActionPicker({
  value,
  onChange,
  ns,
  useCatalog,
  rowAction,
  locked = false,
  newIds,
}: {
  value: PickerAction[];
  onChange: (next: PickerAction[]) => void;
  ns: string;
  useCatalog: () => CatalogQuery;
  /** Per-row control the owner renders — the action's reorder button. */
  rowAction?: (action: PickerAction) => React.ReactNode;
  /**
   * Read-only: a link change is staged and the set shown is the *proposed* one.
   * It stays frozen until a second administrator approves or counter-proposes,
   * so nothing here may add or remove — matching the Django edit page.
   */
  locked?: boolean;
  /**
   * Ids the pending proposal would *add*. Marked inline, the way Django's
   * "Liste proposée" marks them, so a reviewer sees where an addition lands in
   * the order rather than only that one exists.
   */
  newIds?: ReadonlySet<string>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {t(locked ? `${ns}.picker.lockedHint` : `${ns}.picker.hint`)}
        </p>
        {!locked && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
          >
            <Plus className="mr-1 size-4" aria-hidden />
            {t(`${ns}.picker.add`)}
          </Button>
        )}
      </div>

      {value.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-5 text-center text-sm text-muted-foreground">
          {/* A *locked* empty list is a proposal to unlink everything, which
              reads very differently from an untouched new record. */}
          {t(locked ? `${ns}.picker.lockedEmpty` : `${ns}.picker.empty`)}
        </p>
      ) : (
        <ul className="space-y-2">
          {value.map((item) => (
            <li
              key={item.id}
              className="grid grid-cols-[1fr_auto] items-start gap-2.5 rounded-md border border-border bg-card p-2.5"
            >
              <div className="min-w-0">
                <p className="break-words text-sm font-medium">
                  <span className={cn(newIds?.has(item.id) && "text-success")}>
                    {item.name}
                  </span>
                  {newIds?.has(item.id) && (
                    <span className="ml-1.5 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-semibold text-success">
                      {t(`${ns}.picker.newLink`)}
                    </span>
                  )}
                </p>
                <p className="break-words text-xs text-muted-foreground">
                  {item.issue} › {item.goal}
                </p>
              </div>
              <span className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                {rowAction?.(item)}
                {/* Removing is destructive but secondary to the reorder button
                    beside it, so it stays quiet until hovered. */}
                {!locked && (
                  <button
                    type="button"
                    title={t(`${ns}.picker.remove`)}
                    aria-label={t(`${ns}.picker.remove`)}
                    onClick={() =>
                      onChange(value.filter((x) => x.id !== item.id))
                    }
                    className="grid size-8 shrink-0 place-items-center rounded-md border border-transparent text-muted-foreground transition-colors hover:border-destructive/30 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </button>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <AddActionsModal
          current={value}
          ns={ns}
          useCatalog={useCatalog}
          onClose={() => setOpen(false)}
          onConfirm={(next) => {
            onChange(next);
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

function AddActionsModal({
  current,
  ns,
  useCatalog,
  onClose,
  onConfirm,
}: {
  current: PickerAction[];
  ns: string;
  useCatalog: () => CatalogQuery;
  onClose: () => void;
  onConfirm: (next: PickerAction[]) => void;
}) {
  const { t } = useTranslation();
  const catalog = useCatalog();
  const [search, setSearch] = useState("");
  const [issue, setIssue] = useState("");
  const [goal, setGoal] = useState("");
  const originalIds = useMemo(() => new Set(current.map((x) => x.id)), [current]);
  const [pending, setPending] = useState<Record<string, PickerAction>>(() =>
    Object.fromEntries(current.map((x) => [x.id, x])),
  );

  const actions = catalog.data?.actions ?? [];
  const issues = catalog.data?.issues ?? [];

  const goalsByIssue = useMemo(
    () => catalog.data?.goalsByIssue ?? {},
    [catalog.data],
  );
  const goalOptions = issue
    ? goalsByIssue[issue] ?? []
    : Object.values(goalsByIssue).flat();
  const goalIssue = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(goalsByIssue).forEach(([iss, goals]) =>
      goals.forEach((g) => (map[g] = iss)),
    );
    return map;
  }, [goalsByIssue]);

  const countBy = (pred: (a: PickerAction) => boolean) => actions.filter(pred).length;

  const shown = actions.filter((a) => {
    if (issue && a.issue !== issue) return false;
    if (goal && a.goal !== goal) return false;
    if (search && !a.name.toLowerCase().includes(search.trim().toLowerCase()))
      return false;
    return true;
  });

  const toggle = (a: PickerAction, on: boolean) =>
    setPending((p) => {
      const next = { ...p };
      if (on) next[a.id] = a;
      else delete next[a.id];
      return next;
    });

  const selectAllShown = () =>
    setPending((p) => {
      const next = { ...p };
      shown.forEach((a) => (next[a.id] = a));
      return next;
    });

  const confirm = () => {
    // The checked set becomes the list, read in questionnaire order — the
    // catalogue is already in that order, so its index is the sort key. Ids
    // missing from it (a stale link) keep the tail.
    const rank = new Map(actions.map((a, i) => [a.id, i]));
    const selected = Object.values(pending).slice();
    selected.sort(
      (a, b) => (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity),
    );
    onConfirm(selected);
  };

  return (
    <Modal
      title={t(`${ns}.picker.modalTitle`)}
      onClose={onClose}
      className="max-w-xl"
    >
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(`${ns}.picker.searchActions`)}
            className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm"
          />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <SearchSelect
            allLabel={t(`${ns}.picker.allIssues`)}
            value={issue}
            searchPlaceholder={t(`${ns}.picker.searchIssue`)}
            options={issues.map((i) => ({
              value: i,
              label: i,
              count: countBy((a) => a.issue === i),
            }))}
            onChange={(v) => {
              setIssue(v);
              if (goal && goalIssue[goal] !== v) setGoal("");
            }}
          />
          <SearchSelect
            allLabel={t(`${ns}.picker.allGoals`)}
            value={goal}
            searchPlaceholder={t(`${ns}.picker.searchGoal`)}
            options={goalOptions.map((g) => ({
              value: g,
              label: g,
              count: countBy((a) => a.goal === g),
            }))}
            onChange={(v) => {
              setGoal(v);
              if (v && goalIssue[v]) setIssue(goalIssue[v]);
            }}
          />
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>{t(`${ns}.picker.matchCount`, { count: shown.length })}</span>
          <button
            type="button"
            className="font-semibold text-primary hover:underline"
            onClick={selectAllShown}
          >
            {t(`${ns}.picker.selectAll`)}
          </button>
        </div>

        <div className="max-h-[40vh] min-h-[8rem] overflow-y-auto rounded-md border border-border">
          {catalog.isPending ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t(`${ns}.loading`)}
            </p>
          ) : catalog.isError ? (
            <p className="p-6 text-center text-sm text-destructive">
              {t(`${ns}.error`)}
            </p>
          ) : shown.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              {t(`${ns}.picker.noMatch`)}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {shown.map((a) => (
                <li key={a.id}>
                  <label className="flex cursor-pointer items-start gap-3 p-2.5 hover:bg-accent">
                    <input
                      type="checkbox"
                      className="mt-0.5 size-4"
                      checked={Boolean(pending[a.id])}
                      onChange={(e) => toggle(a, e.target.checked)}
                    />
                    <span className="text-sm leading-snug">
                      {a.name}
                      <span className="block text-xs text-muted-foreground">
                        {a.issue} › {a.goal}
                        {originalIds.has(a.id) && (
                          <span className="ml-2 font-bold uppercase tracking-wide text-success">
                            {t(`${ns}.picker.linkedTag`)}
                          </span>
                        )}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
          <span className="text-sm text-muted-foreground">
            {t(`${ns}.picker.selectedCount`, { count: Object.keys(pending).length })}
          </span>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t(`${ns}.admin.cancel`)}
            </Button>
            <Button type="button" onClick={confirm}>
              {t(`${ns}.picker.confirm`)}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/** A searchable single-select dropdown (button + popover with a search box). */
function SearchSelect({
  allLabel,
  value,
  options,
  searchPlaceholder,
  onChange,
}: {
  allLabel: string;
  value: string;
  options: { value: string; label: string; count?: number }[];
  searchPlaceholder: string;
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, [open]);

  const label = value
    ? options.find((o) => o.value === value)?.label ?? value
    : allLabel;
  const items = [{ value: "", label: allLabel }, ...options];
  const filtered = q
    ? items.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase()))
    : items;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2 text-sm"
      >
        <span className="truncate">{label}</span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {open && (
        <div className="absolute z-10 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover shadow-lg">
          <div className="border-b border-border p-2">
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full rounded border border-border bg-background px-2 py-1 text-sm"
            />
          </div>
          <ul className="max-h-52 overflow-y-auto p-1">
            {filtered.map((o) => (
              <li key={o.value || "__all"}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setQ("");
                  }}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-accent",
                    o.value === value && "font-semibold text-primary",
                  )}
                >
                  <span className="truncate">{o.label}</span>
                  {o.value === value ? (
                    <Check className="size-4 shrink-0" aria-hidden />
                  ) : (
                    o.count != null && (
                      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                        {o.count}
                      </span>
                    )
                  )}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="p-3 text-center text-xs text-muted-foreground">—</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
